/**
 * Last known viewport-relative (client) pointer position.
 *
 * Tools that draw their own cursor need to place it the moment they become active, but the pointer
 * position is only obtainable from an event — a component mounting mid-session has no way to ask
 * where the pointer currently is. Recording it continuously means a tool activated by keyboard
 * shortcut, with the pointer already sitting still over a viewport, can render its cursor
 * immediately instead of waiting for the user to jiggle the mouse.
 */
export type PointerPosition = {
  clientX: number;
  clientY: number;
};

let lastPointerPosition: Nullable<PointerPosition> = null;
let subscriberCount = 0;

const record = (event: MouseEvent) => {
  lastPointerPosition = { clientX: event.clientX, clientY: event.clientY };
};

/**
 * Begins recording the pointer position, and returns a matching stop function. Reference counted, so
 * overlapping callers (one per viewport) share a single listener and the last one out removes it.
 *
 * Registered in the capture phase so it still records when a handler downstream stops propagation,
 * and passive because it never calls `preventDefault`.
 */
export const trackPointerPosition = (): (() => void) => {
  if (subscriberCount === 0) {
    document.addEventListener('mousemove', record, { capture: true, passive: true });
  }
  subscriberCount += 1;

  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    subscriberCount -= 1;
    if (subscriberCount === 0) {
      document.removeEventListener('mousemove', record, { capture: true });
      // Drop the stale position too: with nothing tracking, the next reader would otherwise get a
      // position from before the gap in coverage.
      lastPointerPosition = null;
    }
  };
};

/** The most recent pointer position, or `null` if the pointer has not moved since tracking began. */
export const getLastPointerPosition = (): Nullable<PointerPosition> => lastPointerPosition;

/** Test seam — resets module state between cases. */
export const resetPointerPositionForTests = (): void => {
  if (subscriberCount > 0) {
    document.removeEventListener('mousemove', record, { capture: true });
  }
  lastPointerPosition = null;
  subscriberCount = 0;
};
